import React from "react";

import { StageLayout } from "../layouts/StageLayout";
import { PodiumPlotView, readPodiumSlide } from "../plots/podium";
import { registerSlide } from "./registry";
import { SlotFootnote } from "../slots";
import { EditableSlideText } from "./EditableSlideText";

import type { EditableSlideProps } from "./contracts";
import type { PodiumSlideData } from "./renderer-internal-types";
import type { StoredPodiumSlide } from "../plots/podium";

const MUTED = "var(--slide-text-muted, #737373)";

const MAX_ENTRIES = 6;

/**
 * The verdict slide. Text-only messages use the podium plot's existing bars;
 * other tested assets use its board. A slide built with `plot` draws Python's
 * rank, winner, tie and decimals; a slide stored before it draws through the
 * legacy read adapter, from the ranks the entries always carried. The
 * renderer never re-ranks or re-sorts.
 */
function PodiumSlideComponent(props: PodiumSlideData & EditableSlideProps & { plot?: StoredPodiumSlide["plot"] }) {
  const { entries, footnote, editable, onEdit, renderMode } = props;

  const stored: StoredPodiumSlide = {
    type: "podium",
    entries: (entries || []).slice(0, MAX_ENTRIES),
    scoreLabel: props.scoreLabel,
    scoreUnit: props.scoreUnit,
    tie: props.tie,
    tieLabel: props.tieLabel,
    plot: props.plot,
  };
  const { payload, isStamped } = readPodiumSlide(stored);
  const fieldPrefix = isStamped ? "plot.data.items" : "entries";

  return (
    <StageLayout
      preset="podium"
      eyebrowText={props.eyebrow}
      actionTitle={props.actionTitle}
      clientLogoUrl={props.clientLogoUrl}
      eyebrowTextTransform="none"
      editable={editable}
      onEdit={onEdit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          gap: 16,
        }}
      >
        <PodiumPlotView
          data={payload.data}
          size="slide"
          layout={props.itemKind === "text" ? "bars" : "board"}
          heading={props.itemKind === "text" ? props.scoreLabel : null}
          renderMode={renderMode}
          renderLabel={({ item, index, maxLines, style }) => (
            <EditableSlideText
              value={item.label}
              field={`${fieldPrefix}.${index}.label`}
              editable={editable}
              onEdit={onEdit}
              minLines={1}
              maxLines={maxLines}
              lineHeight="1.3"
              tooltipOnTruncate
              renderMode={renderMode}
              style={style}
            />
          )}
        />

        {footnote && footnote.trim() && (
          <SlotFootnote testId="podium-footnote" maxLines={2} lineHeight="1.4" color={MUTED} style={{ textAlign: "center" }} renderMode={renderMode}>
            {footnote.trim()}
          </SlotFootnote>
        )}
      </div>
    </StageLayout>
  );
}

export const PodiumSlide = registerSlide("podium", PodiumSlideComponent);
